import React, {
	Context,
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { callApi, TApiResponse } from '../api'
import { TChannel, IChannel, TChannelActions } from '../types'
import { useAuthContext } from '../auth'
import { cleanLocalStorageReferredData } from '../../utils/helper'
import { useHistory } from 'react-router-dom'

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

const initialChannelState: IChannel = {
	channels: null,
	selectedChannel: null,
	filterChannels: null,
	selectedChannelId: null,
	defaultUserChannel: null,
	loading: false,
	showPayouts: false,
}

const ChannelContext: Context<{
	channel: IChannel
	actions: TChannelActions
}> = createContext({
	channel: initialChannelState,
	actions: {
		getUserChannels: () => {},
		setSelectedChannelId: (channelId) => {},
		updateSelectedChannelId: (channelId) => channelId,
	},
})

export const ChannelProvider = (props: PropsWithChildren<{}>) => {
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setProviderError] = useState<string | null>(null)
	const [selectedChannel, setSelectedChannel] = useState<TChannel>(null)
	const [selectedChannelId, setSelectedChannelId] = useState<number>(null)
	const [channels, setChannels] = useState<TChannel[]>(null)
	const [filterChannels, setFilterChannels] = useState<TChannel[]>(null)
	const [defaultUserChannel, setDefaultUserChannel] = useState<TChannel>(null)
	const { auth } = useAuthContext()
	const history = useHistory()

	const urlParams = new URLSearchParams(window.location.search)
	const inviteCode = urlParams.get('invitecode')
	const referrer = urlParams.get('referrer')

	useEffect(() => {
		if (filterChannels?.length) {
			setSelectedChannelId(
				filterChannels.find((channel: TChannel) => channel.is_user_default)?.id ||
					filterChannels.find((channel: TChannel) => channel.is_brand_default)?.id ||
					channels[0]?.id
			)
		} else if (channels?.length) {
			setSelectedChannelId(
				channels.find((channel: TChannel) => channel.is_user_default)?.id ||
					channels.find((channel: TChannel) => channel.is_brand_default)?.id ||
					channels[0]?.id
			)
		} else {
			setSelectedChannelId(null)
		}
	}, [filterChannels, channels])

	useEffect(() => {
		if (channels?.length && selectedChannelId) {
			setSelectedChannel(channels.find(({ id }) => id === selectedChannelId))
		}
	}, [channels, selectedChannelId])

	const fetchUserChannels = async () => {
		setLoading(true)
		const channelResponse: TApiResponse = await callApi('/channel/list', {})

		const { data } = channelResponse.payload

		if (channelResponse.ok) {
			const newDefaultUserChannel = data.channels.find((channel: TChannel) => channel.is_user_default)
			setLoading(false)
			setProviderError(null)
			setChannels(data.channels)
			setDefaultUserChannel(newDefaultUserChannel)
			setFilterChannels(data.channels.filter((channel: TChannel) => channel.is_brand_default || channel.expert_filter))
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get Channel List!')
		}
	}

	const updateSelectedChannelId = (channelId: number) => {
		if (selectedChannelId === channelId) return channelId

		if (filterChannels?.some((channel) => channel.id === channelId)) {
			setSelectedChannelId(channelId)
			return channelId
		}

		return selectedChannelId
	}

	const getUserChannels = useCallback(fetchUserChannels, [])

	const showPayouts = useMemo(
		() =>
			channels?.reduce(
				(show, { is_expert, integration_config }) => show || (is_expert && !integration_config.no_payouts),
				false
			),
		[channels]
	)

	const fetchJoinToChannel = async ({
		inviteCode,
		referrer,
	}: {
		inviteCode?: string
		referrer?: string
	}): Promise<void> => {
		const response: TApiResponse = await callApi(`/channel/join`, {
			method: 'POST',
			body: { inviteCode, referrer },
		})

		if (response.ok) {
			await fetchUserChannels()
		}

		return
	}

	useEffect(() => {
		if (auth.token) {
			if (inviteCode || referrer) {
				fetchJoinToChannel({ inviteCode, referrer })
					.catch(() => {})
					.finally(() => {
						cleanLocalStorageReferredData()
						history.push({ search: '' })
					})
			} else if (!channels) {
				actions.getUserChannels()
			}
		} else if (!auth.token) {
			setChannels(null)
			setFilterChannels(null)
			setSelectedChannelId(null)
		}
	}, [auth.token])

	const actions: TChannelActions = { getUserChannels, setSelectedChannelId, updateSelectedChannelId }

	return (
		<ChannelContext.Provider
			value={{
				channel: {
					channels,
					filterChannels,
					selectedChannelId,
					loading,
					error,
					defaultUserChannel,
					showPayouts,
					selectedChannel,
				},
				actions,
			}}
		>
			{props.children}
		</ChannelContext.Provider>
	)
}

export const useChannelContext: () => {
	channel: IChannel
	actions: TChannelActions
} = () => {
	return useContext(ChannelContext)
}
